import { useEffect, useState } from 'react';

/**
 * View-only role (Executive): sees every page and all data, performs no action.
 *
 * The server already rejects every write from this role; this is what keeps
 * the UI from offering controls that can only fail. Hide or disable a write
 * control with it — never a read, a filter, a tab or a download.
 *
 * Read from `localStorage.user` rather than a context so the same file works
 * in every microfrontend, including the ones with no AuthContext. This file is
 * identical in each app under apps/ — change them together.
 */
export function isReadOnlyUser(): boolean {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const role = (JSON.parse(raw) as { role?: unknown }).role;
    return typeof role === 'string' && role.trim().toLowerCase() === 'executive';
  } catch {
    return false;
  }
}

export function useReadOnly(): boolean {
  const [readOnly, setReadOnly] = useState(isReadOnlyUser);

  useEffect(() => {
    const refresh = () => setReadOnly(isReadOnlyUser());
    // The shell re-saves the profile every minute and announces it, so a role
    // change reaches an open page without a reload.
    window.addEventListener('esgcaffe:user-permissions-refreshed', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('esgcaffe:user-permissions-refreshed', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  return readOnly;
}
